import { showConfirm } from '../components/ConfirmDialog';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { apiGetContacts, apiDeleteContact, apiGetVenues, type ContactDTO, type VenueDTO } from '../api';
import { useWriteGate } from '../lib/writeGate';
import { useProfileContext } from '../hooks/useProfileContext';
import PageHeader from '../components/PageHeader';
import { withPageIcon } from '../lib/uiIcons';
import PageLoadError from '../components/PageLoadError';

// P3a — role colours are TOKENS (Roadcase family), never hex literals: the
// same five roles keep five distinguishable hues in both themes.
const ROLE_COLORS: Record<string, string> = {
  promoter: 'var(--danger)',
  agent: 'var(--info)',
  press: 'var(--success)',
  label: 'var(--warning)',
  other: 'var(--text-muted)',
};

export default function ContactsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { activeProfileId } = useProfileContext();
  // Capability-aware write gate (lot A2). `/api/contacts` is mounted AFTER the
  // server's global read-only guard (server/index.js:519-525; catalog-write
  // guard at :592-595, router at :597) → DELETE 403s a read-only account
  // (and a viewer: requireCatalogWrite wants editor+ on a band).
  const writeGate = useWriteGate();
  const readOnlyNote = writeGate.readOnlyReason === 'global-read-only' && !writeGate.canWrite;
  const [contacts, setContacts] = useState<ContactDTO[]>([]);
  const [venues, setVenues] = useState<VenueDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  // Raw API message for the shared state's context line (never an invented cause).
  const [loadErrorDetail, setLoadErrorDetail] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setLoadError(false);
    setLoadErrorDetail(null);
    Promise.all([apiGetContacts(), apiGetVenues()])
      .then(([c, v]) => { setContacts(c); setVenues(v); })
      .catch((e: unknown) => {
        console.error(e);
        setLoadError(true);
        setLoadErrorDetail(e instanceof Error ? e.message : String(e));
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  // A contact linked to one or more bands is only visible in those bands; a
  // contact with NO band link is a shared/global contact (visible everywhere).
  // Filter by the active band so a superadmin (who can access every band) does
  // not see every band's private contacts on this page. (2026-10-06)
  const visibleContacts = activeProfileId
    ? contacts.filter((c) => !c.band_ids || c.band_ids.length === 0 || c.band_ids.includes(activeProfileId))
    : contacts;

  const venueName = (id?: string) => venues.find((v) => v.id === id)?.name || '';

  const remove = async (id: string) => {
    if (!writeGate.canWrite) return;
    if (!(await showConfirm(t('contacts.deleteConfirm')))) return;
    await apiDeleteContact(id);
    load();
  };

  // Lot 3a: without its contacts+venues read this page is a grid of nothing,
  // which reads as "this band has no contacts" instead of "the request failed".
  if (loadError) {
    return (
      <PageLoadError title={t('contacts.title')} detail={loadErrorDetail} onRetry={load} />
    );
  }

  return (
    <div className="page">
      <PageHeader
        title={withPageIcon('contacts.title', t('contacts.title'))}
        subtitle={t('contacts.subtitle')}
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={() => navigate('/tour/contacts/new')} disabled={!writeGate.canWrite}>{t('contacts.newContact')}</button>
          </div>
        }
      />

      {readOnlyNote && (
        <p className="text-dim" style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 12px' }}>
          {t('common.readOnlyAccount')}
        </p>
      )}

      {loading ? <div className="loading">{t('contacts.loading')}</div> : (
        <div className="musician-grid" data-contacts-grid>
          {visibleContacts.map((c) => (
            <div
              key={c.id}
              className="musician-card"
              data-contact-card={c.id}
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/tour/contacts/${c.id}`)}
            >
              <div
                className="musician-color-bar"
                style={{ backgroundColor: ROLE_COLORS[c.role || 'other'] || 'var(--text-muted)' }}
              />
              <div className="musician-card-body">
                <h3>{c.name || t('contacts.unnamed')}</h3>
                <p className="musician-role">{c.role || t('contacts.noRole')}</p>
                <div className="musician-details">
                  {c.email && <span>✉ {c.email}</span>}
                  {c.phone && <span>☎ {c.phone}</span>}
                </div>
                {venueName(c.venue_id) && (
                  <p className="instrument-count">📍 {venueName(c.venue_id)}</p>
                )}
                <div className="musician-card-actions">
                  <button
                    className="btn btn-small"
                    onClick={(e) => { e.stopPropagation(); navigate(`/tour/contacts/${c.id}`); }}
                  >
                    {t('contacts.edit')}
                  </button>
                  <button
                    className="btn btn-small btn-danger"
                    disabled={!writeGate.canWrite}
                    onClick={(e) => { e.stopPropagation(); remove(c.id); }}
                  >
                    {t('contacts.delete')}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
